import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../../services/api';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth as firebaseAuth } from '../../firebase/firebase';
import { userAPI } from '../../firebase/schema';

// Async Thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ phone, otp, password, userType, email }, { rejectWithValue, dispatch }) => {
    try {
      const useBackend = process.env.REACT_APP_USE_BACKEND === 'true';
      if (useBackend) {
        const response = await authAPI.login({ phone, otp, password, userType });
        const { token, refreshToken, user } = response.data;

        // Store both tokens and user data
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));

        return { token, refreshToken, user };
      }

      // Firebase-only flow: sign in with email/password and persist minimal profile
      if (!email && !phone) {
        return rejectWithValue({ message: 'Email or phone is required for login in Firebase-only mode', status: 400 });
      }

      // Prefer email for Firebase sign-in
      const identifier = email || phone;
      const fbUserCred = await signInWithEmailAndPassword(firebaseAuth, identifier, password);
      const fbUser = fbUserCred.user;

      // Fetch user profile from Firestore to get the definitive userType
      const userProfile = await userAPI.getUserProfile(fbUser.uid);
      if (!userProfile) {
        return rejectWithValue({ message: 'User profile not found in Firestore', status: 404 });
      }

      const idToken = await fbUser.getIdToken();
      const minimal = { uid: fbUser.uid, name: userProfile.name || fbUser.displayName || '', email: userProfile.email || fbUser.email || identifier, role: userProfile.userType || 'user' };

      localStorage.setItem('token', idToken);
      localStorage.setItem('user', JSON.stringify(minimal));

      // Update Redux user slice via dispatch to ensure consistency
      dispatch({ type: 'auth/updateUserProfile', payload: minimal });

      return { token: idToken, refreshToken: null, user: minimal };
    } catch (error) {
      // Provide structured error info so callers can react to network/server errors
      const status = error.response?.status;
      const serverMessage = error.response?.data?.message;
      const message = serverMessage || error.message || 'Login failed';
      return rejectWithValue(message);
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData, { rejectWithValue }) => {
    try {
      const useBackend = process.env.REACT_APP_USE_BACKEND === 'true';
      if (!useBackend) {
        // In Firebase-only mode registration is handled by Register.jsx; thunk can
        // resolve immediately to keep callers happy.
        return { success: true, message: 'Registration handled by Firebase' };
      }

      const response = await authAPI.register(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const sendOTP = createAsyncThunk(
  'auth/sendOTP',
  async ({ phone }, { rejectWithValue }) => {
    try {
      const response = await authAPI.sendOTP({ phone });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send OTP');
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async ({ phone, otp }, { rejectWithValue }) => {
    try {
      const response = await authAPI.verifyOTP({ phone, otp });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'OTP verification failed');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authAPI.refreshToken({ refreshToken });
      const { token, newRefreshToken } = response.data;
      
      // Update both tokens
      localStorage.setItem('token', token);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      
      return { token, refreshToken: newRefreshToken };
    } catch (error) {
      // Clear all auth data on refresh failure
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return rejectWithValue(error.response?.data?.message || 'Token refresh failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        // Try to invalidate the token on the server
        await authAPI.logout({ refreshToken });
      }
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      // Always clear local storage, even if server request fails
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return true;
    }
  }
);

export const loginWithFirebase = createAsyncThunk(
  'auth/loginWithFirebase',
  async ({ idToken }, { rejectWithValue }) => {
    try {
      const response = await authAPI.login({ idToken });
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
      return response.data.data;
    } catch (error) {
      const status = error.response?.status;
      const serverMessage = error.response?.data?.message;
      const message = serverMessage || error.message || 'Login failed';
      return rejectWithValue(message);
    }
  }
);

// Initial State
const initialState = {
  user: JSON.parse(localStorage.getItem('user')) || null,
  token: localStorage.getItem('token') || null,
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null,
  otpSent: false,
  otpVerified: false,
  registrationStep: 1,
  userType: null, // 'user', 'partner', 'admin', 'technician'
};

// Auth Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUserType: (state, action) => {
      state.userType = action.payload;
    },
    setRegistrationStep: (state, action) => {
      state.registrationStep = action.payload;
    },
    resetOTPState: (state) => {
      state.otpSent = false;
      state.otpVerified = false;
    },
    updateUserProfile: (state, action) => {
      // Overwrite or set the user profile with the provided payload
      state.user = { ...(state.user || {}), ...action.payload };
      localStorage.setItem('user', JSON.stringify(state.user));

      // If a token is provided in the payload (Firebase-only flows), update auth state
      if (action.payload.token) {
        state.token = action.payload.token;
        localStorage.setItem('token', action.payload.token);
        state.isAuthenticated = true;
      }

      // If role is provided, keep userType in sync
      if (action.payload.role) {
        state.userType = action.payload.role;
      }
    },
    simulateLogin: (state, action) => {
      state.user = action.payload;
      
      // Create a fake JWT token for the temporary navigation
      const header = { alg: 'HS256', typ: 'JWT' };
      const payload = { 
        userId: 'fake-user-id', 
        role: action.payload.role,
        // Set expiry to 1 hour from now
        exp: Math.floor(Date.now() / 1000) + (60 * 60),
      };
      const encodedHeader = btoa(JSON.stringify(header));
      const encodedPayload = btoa(JSON.stringify(payload));
      const fakeSignature = 'fake-signature';
      const fakeToken = `${encodedHeader}.${encodedPayload}.${fakeSignature}`;

      state.token = fakeToken;
      state.isAuthenticated = true;
      state.userType = action.payload.role;
      state.error = null;
      localStorage.setItem('user', JSON.stringify(action.payload));
      localStorage.setItem('token', fakeToken);
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.userType = action.payload.user.role;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })

      // Login with Firebase
      .addCase(loginWithFirebase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithFirebase.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.userType = action.payload.user.role;
        state.error = null;
      })
      .addCase(loginWithFirebase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Don't auto-login after registration, require OTP verification
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Send OTP
      .addCase(sendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.otpSent = false;
      })
      .addCase(sendOTP.fulfilled, (state) => {
        state.loading = false;
        state.otpSent = true;
        state.error = null;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.otpSent = false;
      })
      
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.loading = false;
        state.otpVerified = true;
        state.error = null;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.otpVerified = false;
      })
      
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.userType = null;
        state.error = null;
        state.otpSent = false;
        state.otpVerified = false;
        state.registrationStep = 1;
      });
  },
});

export const {
  clearError,
  setUserType,
  setRegistrationStep,
  resetOTPState,
  updateUserProfile,
  simulateLogin,
} = authSlice.actions;

export default authSlice.reducer;